/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { PageResultPersistedContextItem, PersistedContextItem } from '@squiz/dxp-ai-client';
import { PaginationPageLimit } from '@squiz/dxp-ai-types';
import { PERMISSIONS } from '@squiz/dxp-auth';
import { useDebounce } from '@squiz/dxp-console-support';
import {
  API_TABLE_EVENT_PAGE_NEXT,
  API_TABLE_EVENT_PAGE_PREVIOUS,
  ApiTable,
  ApiTableFetchOptions,
  ApiTablePageTokens,
  BUTTON_FORMAT_SOFT,
  BUTTON_FORMAT_TRANSPARENT,
  BUTTON_THEME_DANGER,
  BUTTON_THEME_DEFAULT,
  Button,
  DialogProps,
  ICON_DELETE,
  ICON_DELETE_FOREVER,
  Link,
  TOAST_TYPE_DANGER,
  TOAST_TYPE_SUCCESS,
  TOOLTIP_POSITION_LEFT,
  TableCell,
  Toast,
  ToastProps,
} from '@squiz/sds';
import { hasPermission } from 'apps/console-core/src/Utilities/HasPermission/HasPermission';
import * as ConsoleState from 'dxpConsoleCore-mf/State';
import { useDialogStore, useToastStore } from 'dxpConsoleCore-mf/State';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { from, lastValueFrom } from 'rxjs';
import { AI_IDS, AI_STRINGS } from '../../Constants/Strings.constants';
import { getAiService } from '../../Services/ai';
import { useContextItemDialog } from '../AiContextItemDialog/useContextItemDialog';
import { AiContextItemTableProps } from './AiContextItemTable.props';
import { getColumnValues } from './utils/getColumnValues';
import { getTableColumns } from './utils/getTableColumns';

/**
 * Component to list context items.
 */
export const AiContextItemTable = (props: AiContextItemTableProps): JSX.Element => {
  const { ai, contextId, contextSchema, filter, cacheKey, clearCache } = props;
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const aiService = getAiService();
  const [contextItems, setContextItems] = useState<PageResultPersistedContextItem>();
  const [columnKeys, setColumnKeys] = useState<Array<string>>([]);
  const debouncedFilter = useDebounce(filter, 1000);
  const isPendingRefresh = filter !== debouncedFilter;
  const [deleteProps, setDeleteProps] = useState<DialogProps | null>(null);
  const { updateActiveDialog } = useDialogStore();
  const { updateActiveToast } = useToastStore();
  const contextItemDialog = useContextItemDialog();

  const hasReadPermission = hasPermission(PERMISSIONS.dxpAi.CONTEXT_INSTANCE_READ);
  const hasWritePermission = hasPermission(PERMISSIONS.dxpAi.CONTEXT_INSTANCE_WRITE);

  const { t } = useTranslation();
  const labels = useMemo(() => {
    return {
      apiError: t(AI_STRINGS.apiError) as string,
      deleteContextItem: t(AI_STRINGS.deleteContextItem) as string,
      deleteContextItemConfirmation: t(AI_STRINGS.deleteContextItemConfirmation),
      toastErrorMessage: t(AI_STRINGS.deleteContextItemFailed) as string,
      toastSuccessMessage: t(AI_STRINGS.deleteContextItemSuccess) as string,
    };
  }, [t]);

  const [emptyText, setEmptyText] = useState<string>(props.labels.emptyText);

  const tableColumns = useMemo(() => {
    const tableColumnKeys: Array<string> = [];
    let columns: Array<TableCell> = [];

    if (contextSchema.properties) {
      columns = getTableColumns(contextSchema.properties).slice(0, props.maxColumns);

      // Add ID as the first column.
      columns.unshift({
        content: `ID`,
        key: `id`,
      });

      columns.forEach((column) => {
        tableColumnKeys.push(column.key);
      });

      if (hasWritePermission) {
        columns.push({
          content: ``,
          key: `delete`,
        });
      }

      setColumnKeys(tableColumnKeys);
    }

    return columns;
  }, [contextSchema.properties, hasWritePermission, props.maxColumns]);

  // Page token object for pagination.
  const pageTokens = useMemo(() => {
    const pageTokens: ApiTablePageTokens = {};

    if (contextItems && contextItems.links) {
      pageTokens.pageAfter = contextItems.links.nextPage;
      pageTokens.pageBefore = contextItems.links.prevPage;
    }

    return pageTokens;
  }, [contextItems]);

  const getDeleteDialogProps = useCallback(
    (contextItemId: string, updateActiveDialog: Function, deleteContextItem: Function): DialogProps => {
      const closeDeleteDialog = (): void => updateActiveDialog(false);

      return {
        buttons: [
          <Button
            format={BUTTON_FORMAT_SOFT}
            key="cancel-delete-ai-context-item"
            label={t(`Cancel`)}
            onClick={closeDeleteDialog}
            text={t(`Cancel`)}
            theme={BUTTON_THEME_DEFAULT}
          />,
          <Button
            key="delete-ai-context-item"
            label={t(`Delete`)}
            onClick={() => deleteContextItem(contextItemId)}
            text={t(`Delete`)}
            theme={BUTTON_THEME_DANGER}
          />,
        ],
        dialogContent: { renderContent: () => <div>{labels.deleteContextItemConfirmation}</div> },
        heading: t(`Delete context item "${contextItemId}"`),
        icon: ICON_DELETE_FOREVER,
        onClose: () => closeDeleteDialog(),
        stateHandler: ConsoleState.useConsoleServiceStore,
      };
    },
    [labels.deleteContextItemConfirmation, t],
  );

  const tableData = useCallback(
    (options: ApiTableFetchOptions) => {
      setIsLoading(true);
      const { eventType, pageSize } = options;
      let cursor = 0;
      if (eventType === API_TABLE_EVENT_PAGE_NEXT && pageTokens.pageAfter) {
        cursor = parseInt(pageTokens.pageAfter);
      }
      if (eventType === API_TABLE_EVENT_PAGE_PREVIOUS && pageTokens.pageBefore) {
        cursor = parseInt(pageTokens.pageBefore);
      }

      lastValueFrom(
        from(
          aiService.apiClient.chatbotContextItem.list({
            aiId: ai.id,
            contextId,
            cursor,
            filter: debouncedFilter,
            pageSize: pageSize as PaginationPageLimit,
          }),
        ),
      )
        .then((response) => {
          setContextItems(response);
          setIsLoading(false);
          return true;
        })
        .catch((e) => {
          setContextItems({ items: [] });
          setIsLoading(false);
          setEmptyText(labels.apiError);
        });
    },
    [
      ai.id,
      aiService.apiClient.chatbotContextItem,
      contextId,
      debouncedFilter,
      labels.apiError,
      pageTokens.pageAfter,
      pageTokens.pageBefore,
    ],
  );

  const deleteContextItem = useCallback(
    async (contextItemId: string): Promise<void> => {
      const props = getDeleteDialogProps(contextItemId, updateActiveDialog, deleteContextItem);
      setDeleteProps({ ...props, loading: true });
      try {
        await aiService.apiClient.chatbotContextItem.delete(ai.id, contextId, contextItemId);

        updateActiveDialog(false);
        clearCache();

        const toastMessage = `You have successfully deleted "${contextItemId}"`;
        const successToastProps: ToastProps = {
          showToast: true,
          stateHandler: useToastStore,
          title: labels.toastSuccessMessage,
          toastContent: <>{toastMessage}</>,
          toastType: TOAST_TYPE_SUCCESS,
        };

        updateActiveToast(<Toast {...successToastProps} data-id={AI_IDS.deleteToastSuccess} />);
      } catch (e) {
        if (e instanceof Error) {
          setDeleteProps({ ...props, errorMessage: e.message, loading: false });
          // Prepare fail toast props
          const failToastProps: ToastProps = {
            showToast: true,
            stateHandler: useToastStore,
            title: labels.toastErrorMessage,
            toastType: TOAST_TYPE_DANGER,
          };

          updateActiveToast(<Toast {...failToastProps} data-id={AI_IDS.deleteToastFailed} />);
        }
      }
    },
    [
      ai.id,
      aiService.apiClient.chatbotContextItem,
      clearCache,
      contextId,
      getDeleteDialogProps,
      labels.toastErrorMessage,
      labels.toastSuccessMessage,
      updateActiveDialog,
      updateActiveToast,
    ],
  );

  const rows = useMemo(() => {
    if (!contextItems?.items || contextItems.items.length === 0) {
      return [];
    }

    return contextItems.items.map((row: PersistedContextItem) => {
      const columnValues = getColumnValues(contextSchema, row, columnKeys);
      const contextItemId = row.id;
      return {
        ...columnValues,
        delete: (
          <Button
            data-id={`delete-context-item-${contextItemId}`}
            format={BUTTON_FORMAT_TRANSPARENT}
            icon={ICON_DELETE}
            label={labels.deleteContextItem}
            onClick={(): void => {
              setDeleteProps(getDeleteDialogProps(contextItemId, updateActiveDialog, deleteContextItem));
            }}
            theme={BUTTON_THEME_DEFAULT}
            tooltip={{
              label: labels.deleteContextItem,
              position: TOOLTIP_POSITION_LEFT,
            }}
          />
        ),
        id: (
          <Link
            classes="edit-context-item-link"
            data-id={`edit-context-item-link-${contextItemId}`}
            hideIcon
            onClick={(event) => {
              event.preventDefault();
              contextItemDialog.open({
                ai,
                clearCache,
                contextId,
                contextItem: row,
                schema: contextSchema,
              });
            }}
            text={contextItemId}
          />
        ),
        rowId: `context-item-${contextItemId}`,
      };
    });
  }, [
    ai,
    clearCache,
    columnKeys,
    contextId,
    contextItemDialog,
    contextItems,
    contextSchema,
    deleteContextItem,
    getDeleteDialogProps,
    labels.deleteContextItem,
    updateActiveDialog,
  ]);

  useEffect(() => {
    updateActiveDialog(deleteProps);
  }, [deleteProps, updateActiveDialog]);

  if (!hasReadPermission) {
    return <></>;
  }

  return (
    <div className="ai_context-items-table-container">
      <div className="ai_context-items-table-container__table ai_context-items-table">
        <ApiTable
          cacheKey={[cacheKey, debouncedFilter].join('')}
          emptyText={emptyText}
          fetchData={tableData}
          isSlim
          loading={isLoading || isPendingRefresh}
          pageTokens={pageTokens}
          perPageDefault={PaginationPageLimit.small}
          perPageOptions={[PaginationPageLimit.small, PaginationPageLimit.medium, PaginationPageLimit.large]}
          tableContent={{
            columns: tableColumns,
            rows: rows,
          }}
        />
      </div>
    </div>
  );
};
